// ---------------------------------------------------------------------------
// Vorhang vor der ganzen Seite, solange Avliana in Gründung und Test ist.
//
// Das hier ist nur das Gesicht. Die eigentliche Sperre sitzt im Backend
// (backend/services/vorhang.py): Ohne Freigabe-Cookie beantwortet die API jede
// Anfrage mit 403. Ein Vorhang allein in React ließe sich mit einem Klick in
// den Entwicklerwerkzeugen beiseiteschieben — und die Registrierung, über die
// am 25.09.2026 ein Fremder hereinkam, ist ohnehin ein reiner API-Aufruf.
//
// Steht AUSSERHALB von AuthProvider & Co.: Deren erste Anfragen (/auth/me,
// Merkliste, Ungelesen-Zähler) liefen sonst alle gegen die 403 und würden
// Fehlerzustände setzen, bevor der Vorhang offen ist. Deshalb auch kein
// useLang() — der LanguageProvider liegt weiter innen.
//
// Abhängen: VORHANG_PASSWORT am Render-Dienst löschen. Die Statusabfrage meldet
// dann „nicht aktiv" und diese Komponente reicht einfach durch.
// ---------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { t as tr, resolveInitialLang, isRtlLang } from "@/lib/i18n";
import AvlianaLogo from "@/components/AvlianaLogo";

const EREIGNIS = "avliana:vorhang-zu";

// Läuft die Freigabe mitten in der Sitzung ab (30 Tage) oder wird das Passwort
// am Dienst gewechselt, antwortet die API plötzlich mit 403. Dann den Vorhang
// wieder zuziehen statt die Seite in Fehlermeldungen versinken zu lassen.
api.interceptors.response.use(undefined, (fehler) => {
  if (fehler?.response?.status === 403 && fehler.response.data?.detail === "vorhang_geschlossen") {
    window.dispatchEvent(new Event(EREIGNIS));
  }
  return Promise.reject(fehler);
});

function noindexSetzen() {
  // Die Snapshots und die Hülle tragen eigene robots-Angaben; solange der
  // Vorhang hängt, soll keine Fassung in den Index.
  let meta = document.querySelector('meta[name="robots"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "robots");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", "noindex,nofollow");
}

export default function Vorhang({ children }) {
  // "pruefen" | "zu" | "offen"
  const [stand, setStand] = useState("pruefen");

  useEffect(() => {
    let aktiv = true;
    api.get("/vorhang/status")
      // Nur zuziehen, wenn der Server es AUSDRÜCKLICH sagt. Eine Antwort ohne
      // beide Felder (Testattrappen, ein Proxy mit Fehlerseite) zählt als offen —
      // die Daten schützt ohnehin der Server, nicht diese Abfrage.
      .then(({ data }) => {
        if (aktiv) setStand(data?.aktiv === true && data?.offen === false ? "zu" : "offen");
      })
      // Backend nicht erreichbar: Seite zeigen. Die Daten schützt der Server
      // trotzdem — hier ginge es nur um die Optik, und ein ewiger Ladekreis
      // wäre die schlechtere davon.
      .catch(() => { if (aktiv) setStand("offen"); });
    const zu = () => setStand("zu");
    window.addEventListener(EREIGNIS, zu);
    return () => { aktiv = false; window.removeEventListener(EREIGNIS, zu); };
  }, []);

  if (stand === "offen") return children;
  if (stand === "pruefen") return <div className="min-h-screen bg-sand" />;
  return <VorhangSeite />;
}

function VorhangSeite() {
  const [lang] = useState(resolveInitialLang);
  const t = (k) => tr(lang, `vorhang.${k}`);
  const [passwort, setPasswort] = useState("");
  const [fehler, setFehler] = useState("");
  const [sendet, setSendet] = useState(false);

  useEffect(() => {
    noindexSetzen();
    document.title = "Avliana";
    document.documentElement.lang = lang;
    document.documentElement.dir = isRtlLang(lang) ? "rtl" : "ltr";
  }, [lang]);

  const absenden = async (e) => {
    e.preventDefault();
    if (!passwort || sendet) return;
    setSendet(true);
    setFehler("");
    try {
      await api.post("/vorhang/oeffnen", { passwort });
      // Neu laden statt nur umschalten: Alle Provider starten dann sauber mit
      // Freigabe, statt Zustände aus der Zeit vor dem Vorhang mitzuschleppen.
      window.location.reload();
    } catch (err) {
      const status = err?.response?.status;
      setFehler(status === 401 ? t("wrong") : status === 429 ? t("too_many") : t("error"));
      setSendet(false);
    }
  };

  return (
    <main className="min-h-screen bg-sand text-anthracite grid place-items-center px-4" data-testid="vorhang">
      <div className="w-full max-w-sm text-center">
        <AvlianaLogo className="h-12 w-auto mx-auto mb-8" />
        <h1 className="font-heading text-3xl mb-3">{t("title")}</h1>
        <p className="text-anthracite/70 leading-relaxed mb-10">{t("text")}</p>
        <form onSubmit={absenden} className="text-start">
          <label htmlFor="vorhang-passwort" className="block text-xs font-semibold uppercase tracking-[0.12em] text-anthracite/60 mb-2">
            {t("label")}
          </label>
          <input
            id="vorhang-passwort"
            type="password"
            autoComplete="current-password"
            value={passwort}
            onChange={(e) => setPasswort(e.target.value)}
            className="w-full rounded-lg border border-anthracite/15 bg-white dark:bg-sand-section px-4 py-3 focus:outline-none focus:ring-2 focus:ring-terracotta/40"
            data-testid="vorhang-passwort"
          />
          {fehler && <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">{fehler}</p>}
          <button
            type="submit"
            disabled={!passwort || sendet}
            className="mt-4 w-full rounded-lg bg-terracotta text-white font-semibold py-3 disabled:opacity-50"
            data-testid="vorhang-absenden"
          >
            {t("button")}
          </button>
        </form>
      </div>
    </main>
  );
}
